import 'angular';
import * as _ from 'lodash-es';
import Features from '../feature-manager/Features';
import AppConstants from '../gorillaboy/AppConstants';
import { RootScope } from '../types/angular-types';
import {GetTasksResponse, CreateTaskResponse, CreateTaskAssignmentResponse, GetTaskResponse, Task, Action} from '../types/TaskTypes';

interface TasksService {
    takeAction: (TaskID: string, ActionID: string) => ng.IPromise<GetTaskResponse>;
    deleteTask: (TaskID: string) => ng.IPromise<any>;
    createTask: (title: string, description: string, relatedResId: string, taskDefId: string) => ng.IPromise<GetTaskResponse>;
    createTaskAssignment: (taskId: string, principalId: string, principalType: string) => ng.IPromise<CreateTaskAssignmentResponse>;
    getTasks : (resourceId: string, sortBy: string, sortorder: string, marker: string) => ng.IPromise<GetTasksResponse>;
    getAssignedTasks : (marker: string) => ng.IPromise<GetTasksResponse>;
    updateTask : (TaskID: string, Title: string, description: string) => ng.IPromise<any>;
    updateTaskMetadata: (TaskID: string, metadata: string) => ng.IPromise<any>;
    getArchivedTasks : (marker: string) => ng.IPromise<GetTasksResponse>;
}

function TasksServiceFactory($http: ng.IHttpService,
                             $q: ng.IQService,
                             $translate: ng.translate.ITranslateService,
                             $rootScope: RootScope,
                             $log: ng.ILogService,
                             EndpointService: any,
                             FileService,
                             DocumentService,
                             authenticatedUser,
                             FeatureManagerService,
                             UserCollectionService) {

    let approveAndRejectAction: Action[] = [{ ID: AppConstants.TASK_ACTION_IDS.APPROVE }, {ID: AppConstants.TASK_ACTION_IDS.REJECT}];
    let feedbackRequestActions: Action[] = [{ID: AppConstants.TASK_ACTION_IDS.GIVEFEEDBACK}, {ID: AppConstants.TASK_ACTION_IDS.REJECTFEEDBACKREQUEST}];
    let completeAction: Action[] = [{ ID: AppConstants.TASK_ACTION_IDS.COMPLETE}];

    function computeTasks(taskList: Task[]) {
        let userInfo = authenticatedUser.getUserInfo();
        if (!userInfo) {
            $log.error('Cannot compute tasks when the current user info is null')
            return;
        }
        
        let userId = userInfo.Id;
        taskList.forEach(function(task) {

            // If name is empty, it means user doesn't have access to resource
            let key = "",
                params = {},
                folderStr = $translate.instant('FILE_TYPES.FOLDER'),
                documentStr = $translate.instant('TOP_NAV.DOCUMENT');

            // Default has no action
            task.Actions = [];

            if (task.TaskDefinitionId === AppConstants.TASKDEF_IDS.REQUEST_ACCESS
                || task.TaskDefinitionId === AppConstants.TASKDEF_IDS.REQUEST_FEEDBACK
                || task.TaskDefinitionId === AppConstants.TASKDEF_IDS.TRANSFER_RESOURCE_OWNERSHIP) {
                if (task.RelatedResourceType === AppConstants.TASK_RELATED_RESOURCE_TYPES.DOCUMENT) {
                    let anchorValue = (!task.RelatedResourceName) ? documentStr : task.RelatedResourceName;
                    task.Title = $translate.instant(`TASKS.LIST_VIEW.TASK_DEF_TITLES.REQUEST_ACCESS.DOCUMENT`);
                    task.RelatedResourceLink = `<a class="wd-blue-link" href="#/document/${task.RelatedResourceId}">${_.escape(anchorValue)}</a>`

                } else if (task.RelatedResourceType === AppConstants.TASK_RELATED_RESOURCE_TYPES.FOLDER) {
                    let anchorValue = (!task.RelatedResourceName) ? folderStr : task.RelatedResourceName;
                    task.Title = $translate.instant(`TASKS.LIST_VIEW.TASK_DEF_TITLES.REQUEST_ACCESS.FOLDER`);
                    task.RelatedResourceLink = `<a class="wd-blue-link" href="#/folder/${task.RelatedResourceId}">${_.escape(anchorValue)}</a>`
                }

                // Actions are only for assignee
                if (userId !== task.CreatorId) {
                    task.Actions = task.TaskDefinitionId === AppConstants.TASKDEF_IDS.REQUEST_FEEDBACK ?
                        feedbackRequestActions :  approveAndRejectAction;
                    task.Actions.forEach(function(action){
                        action.TextMapping = $translate.instant('TASKS.LIST_VIEW.ACTIONS.' + action.ID);
                    });
                }
            } else if (task.TaskDefinitionId === AppConstants.TASKDEF_IDS.NOTIFY_ADMIN_TO_DO) {
                // Actions are only for assignee
                if (userId !== task.CreatorId) {
                    task.Actions = completeAction;
                    task.Actions.forEach(function(action){
                        action.TextMapping = $translate.instant('TASKS.LIST_VIEW.ACTIONS.' + action.ID);
                    });
                }
                
            }
        });
    }

    function getResourceFetchPromises(taskList: Task[], viewType: string) {
        function filterFn(task: Task) {
            if (task.TaskDefinitionId === AppConstants.TASKDEF_IDS.REQUEST_ACCESS &&
                ((viewType === AppConstants.Tasks.Views.ArchivedTasks && task.State === 'APPROVED_STATE') || viewType === AppConstants.Tasks.Views.AssignedTasks)) {
                return !task.RelatedResourceName; //Only make the call if the RelatedResourceName is not present
            }
            if (task.TaskDefinitionId === AppConstants.TASKDEF_IDS.TRANSFER_RESOURCE_OWNERSHIP ||
                task.TaskDefinitionId === AppConstants.TASKDEF_IDS.REQUEST_FEEDBACK) {
                return !task.RelatedResourceName; //Only make the call if the RelatedResourceName is not present
            }
            return false;
        }

        const folderIds = _.compact(taskList.filter(task => task.RelatedResourceType === AppConstants.TASK_RELATED_RESOURCE_TYPES.FOLDER)
            .filter(filterFn)
            .map(task => task.RelatedResourceId));

        const documentIds = _.compact(taskList.filter(task => task.RelatedResourceType === AppConstants.TASK_RELATED_RESOURCE_TYPES.DOCUMENT)
            .filter(filterFn)
            .map(task => task.RelatedResourceId));

        let uniqueFolderIds = _.uniq(folderIds);
        let uniqueDocumentIds = _.uniq(documentIds);
        const MAX_ITEMS_PER_BATCH = 20;
        const promises = [];
        for (var i = 0; i < uniqueFolderIds.length; i += MAX_ITEMS_PER_BATCH){
            const folderBatch = uniqueFolderIds.slice(i, i + MAX_ITEMS_PER_BATCH);
            promises.push(FileService.batchGetFolder(folderBatch));
        }

        for (var i = 0; i < uniqueDocumentIds.length; i += MAX_ITEMS_PER_BATCH){
            const documentBatch = uniqueDocumentIds.slice(i, i + MAX_ITEMS_PER_BATCH);
            promises.push(DocumentService.batchGetDocument(documentBatch));
        }
        return promises;
    }

    function processRelatedResources(taskList: Task[], viewType: string) : ng.IPromise<Task[]> {
        return $q((resolve, reject) => {
            taskList.forEach(task => {
                task.CreatedDate = new Date(task.CreatedTimestamp * 1000);
                task.ModifiedDate = new Date(task.ModifiedTimestamp * 1000);

                //Parse metadata if it exists
                if (task.Metadata) {
                    let metadata = {};
                    try {
                        metadata = JSON.parse(task.Metadata);
                    } catch (e) {
                        $log.error(`Error parsing metadata of Task ${task.Id} with metadata >${task.Metadata}< `);
                    }
                    task.MetadataParsed = metadata;
                }
            });
            const creatorIds = _.compact(taskList.map(task => task.CreatorId));
            const resolverIds =_.compact(taskList.map(task => task.ResolverId));
            let userIds = _.concat(creatorIds, resolverIds);
            userIds = _.uniq(userIds);

            let promises = [];
            promises.push(UserCollectionService.getUserCollection(userIds));
            promises = promises.concat(getResourceFetchPromises(taskList, viewType));

            let prom = $q.all(promises);
            prom.then(responses => {
                let metadataList = [];
                responses.forEach(function(response, idx) {
                    if (idx === 0) {
                        // Response from getUserCollection
                        const Users: any[] = response as any[];
                        const usersMap = {};
                        Users.forEach(function(User) {
                            usersMap[User.Id] = User;
                        });
                        taskList.forEach(task => {
                            task.Creator = usersMap[task.CreatorId];
                            task.Resolver = usersMap[task.ResolverId];
                        });
                    } else {
                        // Responses from batch APIs
                        metadataList = metadataList.concat(response['data'].MetadataList);
                    }
                });

                let resourceMap = {};
                metadataList.forEach(function(metadata) {
                    if (metadata.LatestVersionMetadata) {
                        resourceMap[metadata.Id] = metadata.LatestVersionMetadata.Name;
                    } else {
                        resourceMap[metadata.Id] = metadata.Name;
                    }
                });
                taskList.forEach(function(task) {
                    if (!task.RelatedResourceName) {
                        //Some times the gb service populates the RelatedResourceName in those cases, do not reset
                        //that value to NULL/undefined
                        task.RelatedResourceName = resourceMap[task.RelatedResourceId];
                    }
                });

                computeTasks(taskList);
                resolve(taskList);
            }, reject);
        });
    }

    const service : TasksService = {

        updateTaskMetadata: (taskId: string, metadata: string) => {
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("TasksService.updateTask");
            const promise = $http({
                method: AppConstants.HttpMethods.PATCH,
                url: EndpointService.endpoint(true) + `tasks/${taskId}`,
                data: {
                    Metadata: metadata
                }
            })
            .then((response) => {
                const data : any = response.data;
                return response;
            },(response) => {
                $rootScope.metricServiceInstance.newCounterMetric("TasksService.updateTask.Error");
                $log.error('update task failed due to '  + JSON.stringify(response));
                return $q.reject({
                    httpStatus: response.status,
                    exceptionMessage: response.data
                });
            });
            promise.finally(() => {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        },

        createTask: (title: string, desc: string, relatedResId: string, taskDefId: string) => {
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("TasksService.createTask");
            const promise = $http({
                method: AppConstants.HttpMethods.POST,
                url: EndpointService.endpoint(true) + 'tasks',
                data: {
                    Title: title? title : undefined,
                    Description: desc ? desc : undefined,
                    RelatedResourceId: relatedResId ? relatedResId: undefined,
                    TaskDefinitionId: taskDefId
                }
            })
                .then((response) => {
                    const data : any = response.data;
                    if (data && data.Task) {
                        return {
                            Task: data.Task,
                            httpStatus: response.status
                        } as GetTaskResponse;
                    }
                    return {
                        Task: null,
                        httpStatus: response.status
                    } as GetTaskResponse;
                }, (response) => {
                    $rootScope.metricServiceInstance.newCounterMetric("TasksService.createTask.Error");
                    return $q.reject({
                        httpStatus: response.status,
                        exceptionMessage: response.data
                    });
                });
            promise.finally(() => {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        },
        createTaskAssignment: (taskId: string, principalId: string, principalType: string) => {
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("TasksService.createTaskAssignment");
            const promise = $http({
                method: AppConstants.HttpMethods.POST,
                url: EndpointService.endpoint(true) + `tasks/${taskId}/assignments`,
                data: {
                    PrincipalId: principalId,
                    PrincipalType: principalType ? principalType : 'USER',
                }
            })
                .then((response) => {
                    const data : any = response.data;
                    if (data ) {
                        return {
                            PrincipalId: data.PrincipalId,
                            PrincipalType: data.PrincipalType,
                            TaskId: data.taskId,
                            httpStatus: response.status
                        } as CreateTaskAssignmentResponse;
                    }
                    return {
                        httpStatus: response.status
                    } as CreateTaskAssignmentResponse;
                },(response) => {
                    $rootScope.metricServiceInstance.newCounterMetric("TasksService.createTaskAssignment.Error");
                    return $q.reject({
                        httpStatus: response.status,
                        exceptionMessage: response.data
                    });
                });
            promise.finally(() => {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        },
        updateTask: (taskId: string, title: string, description: string) => {
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("TasksService.updateTask");
            const promise = $http({
                method: AppConstants.HttpMethods.PATCH,
                url: EndpointService.endpoint(true) + `tasks/${taskId}`,
                data: {
                    Title: title,
                    Description: description
                }
            })
                .then((response) => {
                    const data : any = response.data;
                    return response;
                },(response) => {
                    $rootScope.metricServiceInstance.newCounterMetric("TasksService.updateTask.Error");
                    $log.error('update task failed due to '  + JSON.stringify(response));
                    return $q.reject({
                        httpStatus: response.status,
                        exceptionMessage: response.data
                    });
                });
            promise.finally(() => {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        },
        getTasks: (resourceId: string, sortBy: string, sortOrder: string, marker: string) => {
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("TasksService.getTasks");
            const promise = $http.get(EndpointService.endpoint(true) + 'tasks', {
                params: {
                    resourceId: resourceId? resourceId: undefined,
                    marker: marker ? marker: undefined,
                    sortBy: sortBy ? sortBy : 'DATE',
                    sortOrder: sortOrder ? sortOrder : 'DESCENDING',
                    includeOnly: "OWNED"
                }
            })
                .then((response) => {
                    const data : any = response.data;
                    if (data && data.Tasks) {
                        //Always resolves
                        return processRelatedResources(data.Tasks, AppConstants.Tasks.Views.MyTasks)
                            .then((taskList: Task[]) => {
                                return {
                                    Tasks: taskList,
                                    Marker: data.Marker,
                                    httpStatus: response.status
                                } as GetTasksResponse;
                            });
                    }
                    return {
                        Tasks: [],
                        Marker: null,
                        httpStatus: response.status
                    } as GetTasksResponse;
                }, (response) => {
                    $rootScope.metricServiceInstance.newCounterMetric("TasksService.getTasks.Error");
                    return $q.reject({
                        httpStatus: response.status,
                        exceptionMessage: response.data
                    });
                });
            promise.finally(() => {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        },

        /**
         * This method will fetch the assigned tasks for the caller.
         *
         */
        getAssignedTasks: (marker: string) => {
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("TasksService.getAssignedTasks");
            const promise = $http.get(EndpointService.endpoint(true) + 'tasks', {
                params: {
                    marker: marker ? marker : undefined,
                    sortBy: 'DATE',
                    sortOrder: 'DESCENDING',
                    includeOnly: "ASSIGNED"
                }
            })
                .then((response) => {
                    const data : any = response.data;
                    if (data && data.Tasks) {
                        //Always resolves
                        return processRelatedResources(data.Tasks, AppConstants.Tasks.Views.AssignedTasks)
                            .then((taskList: Task[]) => {
                                return {
                                    Tasks: taskList,
                                    Marker: data.Marker,
                                    httpStatus: response.status
                                } as GetTasksResponse;
                            });
                    }
                    return {
                        Tasks: [],
                        Marker: null,
                        httpStatus: response.status
                    } as GetTasksResponse;
                }, (response) => {
                    $rootScope.metricServiceInstance.newCounterMetric("TasksService.getAssignedTasks.Error");
                    return $q.reject({
                        httpStatus: response.status,
                        exceptionMessage: response.data
                    });
                });
            promise.finally(() => {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        },

        /**
         * This method will fetch the archived tasks for the caller.
         *
         */
        getArchivedTasks: (marker: string) => {
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("TasksService.getArchivedTasks");
            const promise = $http.get(EndpointService.endpoint(true) + 'tasks', {
                params: {
                    marker: marker ? marker : undefined,
                    includeOnly: "ARCHIVED"
                }
            })
                .then((response) => {
                    const data : any = response.data;
                    if (data && data.Tasks) {
                        //Always resolves
                        return processRelatedResources(data.Tasks, AppConstants.Tasks.Views.ArchivedTasks)
                            .then((taskList: Task[]) => {
                                return {
                                    Tasks: taskList,
                                    Marker: data.Marker,
                                    httpStatus: response.status
                                } as GetTasksResponse;
                            });
                    }
                    return {
                        Tasks: [],
                        Marker: null,
                        httpStatus: response.status
                    } as GetTasksResponse;
                }, (response) => {
                    $rootScope.metricServiceInstance.newCounterMetric("TasksService.ArchivedTasks.Error");
                    return $q.reject({
                        httpStatus: response.status,
                        exceptionMessage: response.data
                    });
                });
            promise.finally(() => {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        },

        takeAction: (TaskID: string, ActionId: string) => {
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("TasksService.takeAction");
            const promise = $http({
                method: AppConstants.HttpMethods.POST,
                url: EndpointService.endpoint(true) + `tasks/${TaskID}/actions`,
                data: {
                    ActionId: ActionId
                }
            })
                .then((response) => {
                    const data : any = response.data;
                    return {
                        Task: data && data.Task,
                        httpStatus: response.status
                    } as GetTaskResponse;
                }, (response) => {
                    $rootScope.metricServiceInstance.newCounterMetric("TasksService.takeAction.Error");
                    return $q.reject({
                        httpStatus: response.status,
                        exceptionMessage: response.data
                    });
                });
            promise.finally(() => {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        },


        deleteTask: (TaskID: string) => {
            var latencyEvent = $rootScope.metricServiceInstance.newLatencyMetric("TasksService.deleteTask");
            const promise = $http.delete(EndpointService.endpoint(true) + `tasks/${TaskID}`)
                .then((response) => {
                    const data : any = response.data;
                    return response;
                }, (response) => {
                    $rootScope.metricServiceInstance.newCounterMetric("TasksService.deleteTask.Error");
                    return $q.reject(response);
                });
            promise.finally(() => {
                latencyEvent.stopAndEmitMetric();
            });
            return promise;
        }
    };

    return service;
}

TasksServiceFactory.$inject = ['$http',
    '$q',
    '$translate',
    '$rootScope',
    '$log',
    'EndpointService',
    'FileService',
    'DocumentService',
    'authenticatedUser',
    'FeatureManagerService',
    'UserCollectionService'
];

angular.module('GorillaBoy.Tasks')
    .factory('TasksService', TasksServiceFactory);

export default TasksService;
